import * as React from 'react';
import { AnimatePresence, motion } from "framer-motion";
import { YouTubeEmbed } from './YouTubeEmbed';

function findYouTubeEmbedProps(node) {
  let match;

  React.Children.forEach(node, (child) => {
    if (match || !React.isValidElement(child)) {
      return;
    }

    if (child.type === YouTubeEmbed) {
      match = child.props;
      return;
    }

    match = findYouTubeEmbedProps(child.props.children);
  });

  return match;
}

export function Carousel({ children, autoRotateMs = 12000 }) {
  const [[activeIndex, direction], setActiveIndex] = React.useState([0, 0]);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const carouselRef = React.useRef(null);
  const items = React.Children.toArray(children);

  // we want the scope to be always to be in the scope of the array so that the carousel is endless
  const indexInArrayScope = ((activeIndex % items.length) + items.length) % items.length;

  // so that the carousel is endless, we need to repeat the items twice
  // then, we slice the the array so that we only have 3 items visible at the same time
  const visibleItems = [...items, ...items].slice(
    indexInArrayScope,
    indexInArrayScope + 3
  );
  const handleClick = React.useCallback((newDirection) => {
    setActiveIndex((prevIndex) => [prevIndex[0] + newDirection, newDirection]);
  }, []);

  const handlePlaybackEnd = React.useCallback((playbackState = {}) => {
    if (playbackState.hasSound) {
      setHasUserInteracted(true);
    }

    handleClick(1);
  }, [handleClick]);

  React.useEffect(() => {
    const node = carouselRef.current;

    if (!node || typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting && entry.intersectionRatio >= 0.55);
      },
      {
        threshold: [0, 0.55, 1]
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const markInteracted = () => {
      setHasUserInteracted(true);
    };

    window.addEventListener("pointerdown", markInteracted, { once: true });
    window.addEventListener("keydown", markInteracted, { once: true });

    return () => {
      window.removeEventListener("pointerdown", markInteracted);
      window.removeEventListener("keydown", markInteracted);
    };
  }, []);

  const centerItem = visibleItems[1];
  const activeYouTubeProps = findYouTubeEmbedProps(centerItem);
  const playbackMode = React.isValidElement(centerItem) ? centerItem.props.playbackMode : "timer";
  const durationMs = React.isValidElement(centerItem) ? centerItem.props.durationMs : undefined;

  React.useEffect(() => {
    if (!isInView || items.length <= 1) {
      return undefined;
    }

    if (playbackMode === "event" && durationMs == null) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      handleClick(1);
    }, durationMs ?? autoRotateMs);

    return () => window.clearTimeout(timeoutId);
  }, [autoRotateMs, durationMs, handleClick, isInView, items.length, playbackMode, indexInArrayScope]);

  return (
    <div className="main-wrapper" ref={carouselRef}>
      <div className="wrapper">
        {/*AnimatePresence is necessary to show the items after they are deleted because only max. 3 are shown*/}
        <AnimatePresence mode="popLayout" initial={false}>
          {visibleItems.map((item, index) => {
            const position = index === 0 ? "left" : index === 1 ? "center" : "right";
            const itemIndex = (indexInArrayScope + index) % items.length;

            // The layout prop makes the elements change its position as soon as a new one is added
            // The key tells framer-motion that the elements changed its position
            return (
              <motion.div
                key={`carousel-item-${itemIndex}`}
                layout
                custom={{
                  direction,
                  position
                }}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 1 }}
              >
                {React.isValidElement(item)
                  ? React.cloneElement(item, {
                      hasUserInteracted,
                      isActive: position === "center",
                      isPlaybackEnabled: isInView,
                      onPlaybackEnd: handlePlaybackEnd,
                      previewYouTube: true
                    })
                  : item}
              </motion.div>
            );
          })}
        </AnimatePresence>
        {activeYouTubeProps ? (
          <div className="carouselSharedPlayer">
            <YouTubeEmbed
              {...activeYouTubeProps}
              hasUserInteracted={hasUserInteracted}
              isActive
              isPlaybackEnabled={isInView}
              onPlaybackEnd={handlePlaybackEnd}
              previewOnly={false}
            />
          </div>
        ) : null}
      </div>
      <div>
        <motion.button className="CarouselBackwards" aria-label="Previous Video" whileTap={{ scale: 0.8 }} onClick={() => handleClick(-1)}/>
        <motion.button className="CarouselForward" aria-label="Next Video" whileTap={{ scale: 0.8 }} onClick={() => handleClick(1)}/>
      </div>
    </div>
  );
}

const variants = {
  enter: ({ direction }) => {
    return { scale: 0.2, x: direction < 1 ? 50 : -50, opacity: 0 };
  },
  center: ({ position }) => {
    return {
      scale: position === "center" ? 1 : 0.7,
      x: 0,
      opacity: 1
    };
  },
  exit: ({ direction }) => {
    return { scale: 0.2, x: direction < 1 ? -50 : 50, opacity: 0 };
  }
};
